import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../components/feature/ProtectedRoute';
import { AdminRoute } from '../components/feature/AdminRoute';

// Lazy load pages
const HomePage = lazy(() => import('../pages/home/page'));
const AProposPage = lazy(() => import('../pages/a-propos/page'));
const MinisteresPage = lazy(() => import('../pages/ministeres/page'));
const RegarderPage = lazy(() => import('../pages/regarder/page'));
const RessourcesPage = lazy(() => import('../pages/ressources/page'));
const DonnerPage = lazy(() => import('../pages/donner/page'));
const VisitePage = lazy(() => import('../pages/visite/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const DevenirMembrePage = lazy(() => import('../pages/devenir-membre/page'));
const DemandeComptePage = lazy(() => import('../pages/demande-compte/page'));
const BienvenuePage = lazy(() => import('../pages/bienvenue/page'));
const MonCompteLayout = lazy(() => import('../pages/mon-compte/layout'));
const MonCompteDashboard = lazy(() => import('../pages/mon-compte/page'));
const MonCompteProfil = lazy(() => import('../pages/mon-compte/profil/page'));
const MonCompteEvenements = lazy(() => import('../pages/mon-compte/evenements/page'));
const MonCompteGroupes = lazy(() => import('../pages/mon-compte/groupes/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const ForgotPasswordPage = lazy(() => import('../pages/mot-de-passe-oublie/page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Blog pages
const BlogListPage = lazy(() => import('../pages/blog/page'));
const BlogDetailPage = lazy(() => import('../pages/blog/[slug]/page'));
const PublicEventsPage = lazy(() => import('../pages/evenements/page'));
const EventDetailPage = lazy(() => import('../pages/evenements/[slug]/page'));
const AdminComitePage = lazy(() => import('../pages/admin/comite/index'));

// Admin pages
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminDemandesPage = lazy(() => import('../pages/admin/demandes/page'));
const AdminDemandesComptesPage = lazy(() => import('../pages/admin/demandes-comptes/page'));
const AdminMembresPage = lazy(() => import('../pages/admin/membres/page'));
const AdminVisitesPage = lazy(() => import('../pages/admin/visites/page'));
const AdminPrieresPage = lazy(() => import('../pages/admin/prieres/page'));
const AdminNewsletterPage = lazy(() => import('../pages/admin/newsletter/page'));
const CRMContactsPage = lazy(() => import('../pages/admin/people/page'));
const CRMContactDetailPage = lazy(() => import('../pages/admin/people/[id]/page'));
const AdminContenuPage = lazy(() => import('../pages/admin/contenu/page'));
const AdminEditPageContent = lazy(() => import('../pages/admin/contenu/[slug]/page'));
const AdminArticlesPage = lazy(() => import('../pages/admin/articles/page'));
const AdminPrierePage = lazy(() => import('../pages/priere/page'));
const AdminMurPrierePage = lazy(() => import('../pages/mur-de-priere/page'));
const AdminEditArticlePage = lazy(() => import('../pages/admin/articles/[slug]/page'));
const AdminMediasPage = lazy(() => import('../pages/admin/medias/page'));
const AdminEvenementsPage = lazy(() => import('../pages/admin/evenements/page'));
const AdminEventInscriptionsPage = lazy(() => import('../pages/admin/evenements/inscriptions/page'));
const AdminGalleryPage = lazy(() => import('../pages/admin/galerie/page'));
const AdminMinistreresPage = lazy(() => import('../pages/admin/ministeres/page'));
const AdminLeadersPage = lazy(() => import('../pages/admin/leaders/page'));
const AdminPostsListPage = lazy(() => import('../pages/admin/posts/index'));
const AdminPostsNewPage = lazy(() => import('../pages/admin/posts/new'));
const AdminPostsEditPage = lazy(() => import('../pages/admin/posts/[id]/page'));
const AdminParametresPage = lazy(() => import('../pages/admin/parametres/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Suspense fallback={<div>Chargement...</div>}><HomePage /></Suspense>,
  },
  {
    path: '/a-propos',
    element: <Suspense fallback={<div>Chargement...</div>}><AProposPage /></Suspense>,
  },
  {
    path: '/ministeres',
    element: <Suspense fallback={<div>Chargement...</div>}><MinisteresPage /></Suspense>,
  },
  {
    path: '/regarder',
    element: <Suspense fallback={<div>Chargement...</div>}><RegarderPage /></Suspense>,
  },
  {
    path: '/ressources',
    element: <Suspense fallback={<div>Chargement...</div>}><RessourcesPage /></Suspense>,
  },
  {
    path: '/donner',
    element: <Suspense fallback={<div>Chargement...</div>}><DonnerPage /></Suspense>,
  },
  {
    path: '/visite',
    element: <Suspense fallback={<div>Chargement...</div>}><VisitePage /></Suspense>,
  },
  {
    path: '/contact',
    element: <Suspense fallback={<div>Chargement...</div>}><ContactPage /></Suspense>,
  },
  {
    path: '/blog',
    element: <Suspense fallback={<div>Chargement...</div>}><BlogListPage /></Suspense>,
  },
  {
    path: '/blog/:slug',
    element: <Suspense fallback={<div>Chargement...</div>}><BlogDetailPage /></Suspense>,
  },
  {
    path: '/devenir-membre',
    element: <Suspense fallback={<div>Chargement...</div>}><DevenirMembrePage /></Suspense>,
  },
  {
    path: '/priere',
    element: <Suspense fallback={<div>Chargement...</div>}><AdminPrierePage /></Suspense>
  },
  {
    path: '/mur-de-priere',
    element: <Suspense fallback={<div>Chargement...</div>}><AdminMurPrierePage /></Suspense>
  },
  {
    path: '/demande-compte',
    element: <Suspense fallback={<div>Chargement...</div>}><DemandeComptePage /></Suspense>,
  },
  {
    path: '/login',
    element: <Suspense fallback={<div>Chargement...</div>}><LoginPage /></Suspense>,
  },
  {
    path: '/mot-de-passe-oublie',
    element: <Suspense fallback={<div>Chargement...</div>}><ForgotPasswordPage /></Suspense>,
  },
  {
    path: '/reset-password',
    element: <Suspense fallback={<div>Chargement...</div>}><ResetPasswordPage /></Suspense>,
  },
  {
    path: '/bienvenue',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<div>Chargement...</div>}><BienvenuePage /></Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/mon-compte',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<div>Chargement...</div>}><MonCompteLayout /></Suspense>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Suspense fallback={<div>Chargement...</div>}><MonCompteDashboard /></Suspense> },
      { path: 'profil', element: <Suspense fallback={<div>Chargement...</div>}><MonCompteProfil /></Suspense> },
      { path: 'evenements', element: <Suspense fallback={<div>Chargement...</div>}><MonCompteEvenements /></Suspense> },
      { path: 'groupes', element: <Suspense fallback={<div>Chargement...</div>}><MonCompteGroupes /></Suspense> },
    ]
  },
  {
    path: '/admin/dashboard',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminDashboardPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/membres',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminMembresPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/visites',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminVisitesPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/demandes',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminDemandesPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/demandes-comptes',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminDemandesComptesPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/prieres',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminPrieresPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/newsletter',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminNewsletterPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/people',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><CRMContactsPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/people/:id',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><CRMContactDetailPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/contenu',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminContenuPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/contenu/:slug',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminEditPageContent /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/articles',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminArticlesPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/articles/:slug',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminEditArticlePage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/medias',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminMediasPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/comite',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminComitePage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/evenements',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminEvenementsPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/evenements/:id/inscriptions',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminEventInscriptionsPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/evenements',
    element: <Suspense fallback={<div>Chargement...</div>}><PublicEventsPage /></Suspense>,
  },
  {
    path: '/evenements/:slug',
    element: <Suspense fallback={<div>Chargement...</div>}><EventDetailPage /></Suspense>,
  },
  {
    path: '/admin/galerie',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminGalleryPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/ministeres',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminMinistreresPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/leaders',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminLeadersPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/posts',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminPostsListPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/posts/new',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminPostsNewPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/posts/:id',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminPostsEditPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '/admin/parametres',
    element: (
      <AdminRoute>
        <Suspense fallback={<div>Chargement...</div>}><AdminParametresPage /></Suspense>
      </AdminRoute>
    ),
  },
  {
    path: '*',
    element: <Suspense fallback={<div>Chargement...</div>}><NotFoundPage /></Suspense>,
  },
];

export default routes;

